/* eslint-disable react-hooks/exhaustive-deps */
import { Badge, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import { lighten } from '@mui/material/styles';
import { useCallback, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';

import { StatusCode, WebsocketTypes } from '../../api/enumerations';
import { getMessagesUnread } from '../../api/theHive/workOrders';
import {
  IconAccountBalanceMS,
  IconAdminPanelSettingsMS,
  IconArchiveMS,
  IconArrowBackMS,
  IconArrowForwardIosMS,
  IconCalendarMonthMS,
  IconDashboardMS,
  IconEngineeringMS,
  IconHomeMS,
  IconHubMS,
  IconListAltMS,
  IconLocalActivityMS,
  IconLogoutMS,
  IconNotificationsMS,
  IconPersonMS,
  IconPropellerMS,
  IconSettingsMS,
  IconTuneMS,
  IconWarningMS,
} from '../../constants/icons';
import { GlobalContext } from '../../context/global';
import { WebsocketContext } from '../../context/websocketMessage';
import { getCompanySubdomain, matchCompanySubdomain } from '../../helpers';
import { removeLocalStorageToken } from '../../helpers/localStorage';
import { AccessKey, canSee } from '../../helpers/roleAccess';
import { useCurrentRole } from '../../hooks/useCurrentRole';
import { useStoragedJwt } from '../../hooks/useDecodedJwt';
import useErrorMessage from '../../hooks/useErrorMessage';
import {
  ArrowBox,
  BackArrowBox,
  BoxContainer,
  Drawer,
  DrawerHeader,
  LogoutItem,
  MenuButton,
  MenuIcon,
  MenuList,
  MenuListItem,
  OutletContainer,
  StyledLogo,
  StyledTooltip,
  unreadBadgeCollapsedStyles,
  unreadBadgeExpandedStyles,
} from './styles';

const SHOW_ANNOUNCEMENTS_MENU = true;

export default function Menu({
  smallLogo,
}: {
  smallLogo: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showTitle, setShowTitle] = useState(false);

  const {
    setOpenSnackbar,
    setErrorMessage,
    setSnackbarMessage,
    setIsHiveOwner,
    isHiveOwner,
    setBankCompanyData,
  } = useContext(GlobalContext);
  const { setWebsocketMessage } = useContext(WebsocketContext);
  const { getErrorMessage } = useErrorMessage();
  const theme = useTheme();
  const decoded = useStoragedJwt();
  const { lastMessage } = useWebSocket(
    `${process.env.REACT_APP_API_WEBSOCKET_URL}/websockets/${decoded?.user.id}`
  );

  const active = {
    color: lighten(theme.palette.primary.light, 0.3),
    textDecoration: 'none',
  };

  const inactive = {
    color: theme.palette.primary.contrastText,
    textDecoration: 'none',
  };

  const role = useCurrentRole();

  const hiveOwnerMenu: {
    name: string;
    icon: JSX.Element;
    url: string;
    accessKey: AccessKey;
  }[] = [
    { name: 'início', icon: IconHomeMS, url: '/home', accessKey: 'home' },
    {
      name: 'dashboard',
      icon: IconDashboardMS,
      url: '/dashboard',
      accessKey: 'dashboard',
    },
    {
      name: 'arquivados',
      icon: IconArchiveMS,
      url: '/archived',
      accessKey: 'archive',
    },
    { name: 'the hive', icon: IconHubMS, url: '/the-hive', accessKey: 'hive' },
    {
      name: 'pre flight',
      icon: IconPropellerMS,
      url: '/pre-flight',
      accessKey: 'preflight',
    },
    {
      name: 'notificações',
      icon: IconNotificationsMS,
      url: '/notifications',
      accessKey: 'notifications',
    },
    {
      name: 'formulários',
      icon: IconListAltMS,
      url: '/forms',
      accessKey: 'forms',
    },
    {
      name: 'usuários',
      icon: IconPersonMS,
      url: '/users-roles',
      accessKey: 'userPanel',
    },
    {
      name: 'financeiro',
      icon: IconAccountBalanceMS,
      url: '/finance',
      accessKey: 'finance',
    },
  ];

  const isRealPrice = getCompanySubdomain().includes('realprice');

  const menuList = isHiveOwner
    ? hiveOwnerMenu.filter((item) => canSee(item.accessKey, role))
    : [
        { name: 'início', icon: IconHomeMS, url: '/home' },
        { name: 'arquivados', icon: IconArchiveMS, url: '/archived' },
        { name: 'ajustes', icon: IconTuneMS, url: '/adjustment' },
        { name: 'usuários', icon: IconPersonMS, url: '/users' },
        { name: 'formulários', icon: IconListAltMS, url: '/forms' },
        {
          name: 'gestão de usuários',
          icon: IconEngineeringMS,
          url: '/user-management',
        },
        {
          name: 'oversight',
          icon: IconAdminPanelSettingsMS,
          url: '/oversight/clients',
        },
        ...(isRealPrice && SHOW_ANNOUNCEMENTS_MENU
          ? [
              {
                name: 'comunicados',
                icon: IconLocalActivityMS,
                url: '/announcements',
              },
            ]
          : []),
        {
          name: 'agenda',
          icon: IconCalendarMonthMS,
          url: '/agenda',
        },
        {
          name: 'erros reportados',
          icon: IconWarningMS,
          url: '/error-report',
        },
        { name: 'configurações', icon: IconSettingsMS, url: '/configuration' },
      ];

  const handleDrawerOpen = (): void => {
    setOpen(!open);
    if (!open) {
      setTimeout(() => {
        setShowTitle(true);
      }, 150);
    } else {
      setShowTitle(false);
    }
  };

  const handleLogout = (): void => {
    removeLocalStorageToken();
    window.location.reload();
  };

  const fetchUnreadMessages = useCallback(async () => {
    try {
      const unreadNtifications = await getMessagesUnread();
      if (unreadNtifications.detail.description) {
        throw new Error(unreadNtifications.detail.description);
      }
      if (unreadNtifications.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      if (unreadNtifications.data) {
        setUnreadCount(unreadNtifications.data.total_count);
      }
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    }
  }, []);

  useEffect(() => {
    if (!decoded) return;
    const companySubdomain = matchCompanySubdomain();
    const currentCompanyData = decoded.user.companies.find(
      (company) => company.subdomain === companySubdomain
    );

    if (currentCompanyData) {
      setIsHiveOwner(currentCompanyData?.is_hive_owner);
      if (currentCompanyData.is_hive_owner) {
        setBankCompanyData(currentCompanyData);
        fetchUnreadMessages();
      }
    }
  }, []);

  useEffect(() => {
    if (lastMessage) {
      setWebsocketMessage(lastMessage);
      const messageData = JSON.parse(lastMessage.data);
      if (messageData.type === WebsocketTypes.NEW_MESSAGE) {
        const count = Number(messageData.payload?.unread_messages_count) || 0;

        setUnreadCount((prev) => {
          const newCount = messageData.payload?.read_messages
            ? prev - count
            : prev + count;
          return Math.max(newCount, 0);
        });
      }
    }
  }, [lastMessage]);

  return (
    <>
      <BoxContainer>
        <Drawer variant="permanent" open={open}>
          <Box>
            <DrawerHeader>
              <StyledLogo image={smallLogo} title="logo" />
            </DrawerHeader>
            <MenuList>
              <Box>
                {menuList.map((data) => (
                  <MenuListItem key={data.name} disablePadding>
                    <NavLink
                      to={data.url}
                      style={({ isActive }) => (isActive ? active : inactive)}
                    >
                      {!open ? (
                        <StyledTooltip title={data.name} placement="bottom">
                          <MenuButton open={open} disableTouchRipple>
                            <MenuIcon open={open}>
                              {data.name === 'notificações' &&
                              unreadCount > 0 ? (
                                <Box sx={unreadBadgeCollapsedStyles}>
                                  {data.icon}
                                  <Badge badgeContent={unreadCount} />
                                </Box>
                              ) : (
                                data.icon
                              )}
                            </MenuIcon>
                          </MenuButton>
                        </StyledTooltip>
                      ) : (
                        <MenuButton open={open} disableTouchRipple>
                          <MenuIcon open={open}>
                            {data.name === 'notificações' && unreadCount > 0 ? (
                              <Box>
                                {data.icon}
                                <Badge
                                  badgeContent={unreadCount}
                                  sx={unreadBadgeExpandedStyles}
                                />
                              </Box>
                            ) : (
                              data.icon
                            )}
                            {showTitle && data.name}
                          </MenuIcon>
                        </MenuButton>
                      )}
                    </NavLink>
                  </MenuListItem>
                ))}
              </Box>
              <LogoutItem disablePadding>
                {!open ? (
                  <StyledTooltip title="sair" placement="bottom">
                    <MenuButton
                      open={open}
                      disableTouchRipple
                      onClick={handleLogout}
                    >
                      <MenuIcon open={open}>
                        {IconLogoutMS}
                        {open && 'sair'}
                      </MenuIcon>
                    </MenuButton>
                  </StyledTooltip>
                ) : (
                  <MenuButton
                    open={open}
                    disableTouchRipple
                    onClick={handleLogout}
                  >
                    <MenuIcon open={open}>
                      {IconLogoutMS}
                      {open && 'sair'}
                    </MenuIcon>
                  </MenuButton>
                )}
              </LogoutItem>
            </MenuList>
          </Box>
          <BackArrowBox open={open} />
          <ArrowBox open={open} onClick={handleDrawerOpen}>
            {open ? IconArrowBackMS : IconArrowForwardIosMS}
          </ArrowBox>
        </Drawer>
      </BoxContainer>
      <OutletContainer>
        <Outlet />
      </OutletContainer>
    </>
  );
}
